import React, { FC } from 'react';
import './Page.css';
import { PageHeaderLayout } from '@/Shared/UI/Layouts';
import { BreadCrumps } from '@/Shared/UI/Components';
import { useAppSelector } from '@/OneLiners';
import { Routing } from '@/Shared/UI/Routing';

interface PageProps { }


/**
 * =====================================================
 * Page
 * =====================================================
 *
 * Core layout wrapper for all routed pages.
 *
 * Responsibilities:
 * - Defines the global page structure shared across routes
 * - Displays the header layout consistently
 * - Conditionally renders breadcrumbs based on user session state
 * - Hosts the routing outlet where domain pages are rendered
 *
 * This component acts as a structural shell:
 * - No business logic
 * - No side effects
 * - Purely driven by global application state
 *
 * Author: Samano CASTRE
* Date: July 16, 2025
 */
const Page: FC<PageProps> = () => {

    /**
     * =====================================================
     * Session context
     * =====================================================
     *
     * Determines whether the user is considered "online".
     * This status drives:
     * - Page styling (online / offline)
     * - Visibility of navigation helpers such as breadcrumbs
     */
    const isOnline = !!useAppSelector(state => state.sessionState.userSession)?.id;

    /**
     * =====================================================
     * Render
     * =====================================================
     *
     * Layout structure:
     * - Page header (always visible)
     * - Breadcrumbs (only when authenticated)
     * - Routed content rendered inside page body
     */
    return (
        <div className={`Page page ${isOnline ? 'online-page' : 'offline-page'}`} data-testid="Page">
            <PageHeaderLayout />
            {isOnline && <BreadCrumps />}
            <div className='page-body'>
                <Routing />
            </div>
        </div>
    );
}

export default Page;
